import React, { useMemo } from 'react';

import isValid from 'date-fns/isValid';
import { graphql, useFragment } from 'react-relay';

import { Box } from '@atlaskit/primitives/compiled/box';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientResultType } from '@atlassian/search-client/common/types';

import { ProductKeys } from '../../../../../../common/constants/product-keys';
import type { SearchResultProps } from '../../../../../../common/types';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { ProductIcon } from '../../../../../../common/ui/product-icon';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { StatusTargetDateDisplay } from '../../../../../../common/ui/status-target-date-display';
import { TargetDate } from '../../../../../../common/ui/target-date-display';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt } from '../../../../../../common/utils/search-result-utils';
import { getSiteMetadata } from '../../../../../../common/utils/sites';
import {
	formatSpfAskStatus,
	getAskStatusAppearance,
} from '../../../../../../common/utils/spf-ask-status';
import { ResultAnalytics } from '../../../../../../controllers/analytics/result-analytics';
import type { ResultDerivedDataProps } from '../../../result-derived-data';
import type { searchMercuryAsk_AtlassianSearchPage$key } from './__generated__/searchMercuryAsk_AtlassianSearchPage.graphql';

export const SearchMercuryAsk = ({
	preparedResultAttributes,
	intentDetectionTriggered,
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	primarySubProduct,
	appMode,
	mainContentWidth,
	selectedSites,
}: SearchResultProps<searchMercuryAsk_AtlassianSearchPage$key> &
	ResultDerivedDataProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchMercuryAsk_AtlassianSearchPage on SearchResult {
				__typename
				id
				title
				type
				url
				... on SearchResultMercuryAsk {
					lastModifiedDate
					mercuryAsk: ask @optIn(to: "Mercury") {
						askStatus
						targetDate {
							targetDate
							targetDateType
						}
					}
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [product, contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
			...getSiteMetadata(data.id, cloudId, selectedSites),
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type, cloudId, selectedSites]);

	const title = useMemo(() => enrichExcerpt(data.title), [data.title]);
	const headerMetadataParts = useMemo(
		() => [
			[
				'type',
				<Box>
					<SearchResultType type={data.type as SearchClientResultType} />
				</Box>,
			],
			[
				'last-modified',
				data.lastModifiedDate ? (
					<Box>
						<LastModified lastModified={data.lastModifiedDate} />
					</Box>
				) : null,
			],
		],
		[data.type, data.lastModifiedDate],
	);

	const description = useMemo(() => {
		// Mercury nests status/date under `ask`, and `askStatus` serialises as the same
		// UPPER_CASE keys the Passionfruit shape uses, so both backends share one status map.
		const statusKey = data.mercuryAsk?.askStatus;
		const displayedStatus = formatSpfAskStatus(statusKey);

		if (!displayedStatus) {
			return null;
		}

		const targetDate = data.mercuryAsk?.targetDate?.targetDate;
		const targetDateType = data.mercuryAsk?.targetDate?.targetDateType;
		const displayedTargetDate =
			(targetDate && targetDateType && isValid(new Date(targetDate)) && (
				<TargetDate targetDate={targetDate} targetDateType={targetDateType} />
			)) ||
			undefined;

		return (
			<StatusTargetDateDisplay
				displayedStatus={displayedStatus}
				displayedStatusAppearance={getAskStatusAppearance(statusKey)}
				displayedTargetDate={displayedTargetDate}
			/>
		);
	}, [data.mercuryAsk?.askStatus, data.mercuryAsk?.targetDate]);

	return (
		<ResultAnalytics
			preparedResultAttributes={preparedResultAttributes}
			intentDetectionTriggered={intentDetectionTriggered}
			metadata={metadata}
			position={position}
		>
			{({
				onResultClicked,
				onResultContextMenu: onResultCtxMenuClicked,
				onCopyLinkButtonClicked,
				onSummarizeInChatButtonClicked,
				onContextMenuCopyButtonClicked,
				onContextMenuButtonClicked,
			}) => (
				<SearchResult
					{...data}
					product={ProductKeys.Focus}
					productSelected={!!selectedProduct}
					primaryProduct={primaryProduct}
					primarySubProduct={primarySubProduct}
					onResultClicked={onResultClicked}
					onResultContextMenu={onResultCtxMenuClicked}
					onCopyLinkButtonClicked={onCopyLinkButtonClicked}
					onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
					onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
					onContextMenuButtonClicked={onContextMenuButtonClicked}
					url={data.url}
					title={title}
					icon={<ProductIcon product={ProductKeys.Focus} size="medium" />}
					headerMetadataParts={headerMetadataParts}
					description={description}
					cloudId={cloudId}
					position={position}
					appMode={appMode}
					mainContentWidth={mainContentWidth}
					selectedSites={selectedSites}
				/>
			)}
		</ResultAnalytics>
	);
};

export const isSearchMercuryAsk = (
	node?: any,
): node is searchMercuryAsk_AtlassianSearchPage$key => {
	return node?.id?.includes('mercury') && node.__typename === 'SearchResultMercuryAsk';
};
